import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  useTheme,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Divider,
  Stack
} from '@mui/material';
import {
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  Assignment as AssignmentIcon,
  Folder as FolderIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as AccessTimeIcon,
  Event as EventIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { makeApiCall } from '../../config/api';

const HRDashboard = ({ onNavigate }) => {
  const theme = useTheme();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({});
  const [recentActivities, setRecentActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);

  // Load dashboard data
  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load various HR statistics in parallel
      const [
        leaveRequestsRes,
        absenceStatsRes,
        warningStatsRes,
        reviewStatsRes,
        timesheetQueueRes,
        documentStatsRes,
        employeeStatsRes
      ] = await Promise.all([
        makeApiCall('api/leave/requests?status=pending&limit=5'),
        makeApiCall('api/absences/reports/summary'),
        makeApiCall('api/discipline/reports'),
        makeApiCall('api/reviews/stats/overview'),
        makeApiCall('api/time-tracking/admin/approval-queue?limit=5'),
        makeApiCall('api/hr/documents/stats/overview'),
        makeApiCall('api/employees/stats/overview')
      ]);

      const leaveRequests = leaveRequestsRes.ok ? await leaveRequestsRes.json() : { requests: [] };
      const absenceStats = absenceStatsRes.ok ? await absenceStatsRes.json() : { summary: {} };
      const warningStats = warningStatsRes.ok ? await warningStatsRes.json() : { summary: {} };
      const reviewStats = reviewStatsRes.ok ? await reviewStatsRes.json() : { overall: {} };
      const timesheetQueue = timesheetQueueRes.ok ? await timesheetQueueRes.json() : { entries: [] };
      const documentStats = documentStatsRes.ok ? await documentStatsRes.json() : { stats: {} };
      const employeeStats = employeeStatsRes.ok ? await employeeStatsRes.json() : { stats: {} };

      setStats({
        pendingLeaveRequests: leaveRequests.requests?.length || 0,
        totalAbsences: absenceStats.summary?.total_absences || 0,
        totalWarnings: warningStats.summary?.total_warnings || 0,
        completedReviews: reviewStats.overall?.completed_reviews || 0,
        pendingTimesheets: timesheetQueue.entries?.length || 0,
        totalDocuments: documentStats.stats?.total_documents || 0,
        totalEmployees: employeeStats.stats?.total_employees || 0,
        averageRating: reviewStats.overall?.average_rating || 0,
        activeWarnings: warningStats.summary?.active_warnings || 0,
        unexcusedAbsences: absenceStats.summary?.unexcused_absences || 0
      });

      const activities = [
        ...(leaveRequests.requests || []).map(req => ({
          type: 'leave_request',
          title: `Leave request from ${req.employee_name}`,
          date: req.requested_at,
          status: req.status,
          icon: <EventIcon />,
          navigateTo: 'leave-management',
          record: req,
          details: [
            { label: 'Employee', value: req.employee_name || 'Unknown' },
            { label: 'Leave Type', value: req.leave_type_name || '-' },
            { label: 'Start Date', value: req.start_date ? new Date(req.start_date).toLocaleDateString() : '-' },
            { label: 'End Date', value: req.end_date ? new Date(req.end_date).toLocaleDateString() : '-' },
            { label: 'Total Days', value: req.total_days != null ? String(req.total_days) : '-' },
            { label: 'Reason', value: req.reason || '-' },
            { label: 'Review Notes', value: req.review_notes || '-' }
          ]
        })),
        ...(timesheetQueue.entries || []).map(entry => ({
          type: 'timesheet',
          title: `Timesheet pending approval - ${entry.employee_name}`,
          date: entry.clock_in_time,
          status: entry.approval_status,
          icon: <ScheduleIcon />,
          navigateTo: 'timesheet-calendar',
          record: entry,
          details: [
            { label: 'Employee', value: entry.employee_name || 'Unknown' },
            { label: 'Clock In', value: entry.clock_in_time ? new Date(entry.clock_in_time).toLocaleString() : '-' },
            { label: 'Clock Out', value: entry.clock_out_time ? new Date(entry.clock_out_time).toLocaleString() : '-' },
            { label: 'Total Hours', value: entry.total_hours != null ? String(entry.total_hours) : '-' },
            { label: 'Job', value: entry.job_name || entry.customer_name || '-' },
            { label: 'Notes', value: entry.notes || '-' }
          ]
        }))
      ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 10);

      setRecentActivities(activities);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Failed to load dashboard data');
      // Set fallback data
      setStats({
        pendingLeaveRequests: 3,
        totalAbsences: 8,
        totalWarnings: 5,
        completedReviews: 12,
        pendingTimesheets: 7,
        totalDocuments: 45,
        totalEmployees: 25,
        averageRating: 4.2,
        activeWarnings: 3,
        unexcusedAbsences: 4
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNavigation = (page) => {
    if (onNavigate) {
      onNavigate(page);
    }
  };

  const statsCards = [
    {
      title: 'Total Employees',
      value: stats.totalEmployees || 0,
      icon: <PeopleIcon />,
      color: 'primary.main',
      action: 'View Employees',
      trend: '+2 this month',
      onClick: () => handleNavigation('employees')
    },
    {
      title: 'Pending Leave Requests',
      value: stats.pendingLeaveRequests || 0,
      icon: <EventIcon />,
      color: 'warning.main',
      action: 'View Requests',
      urgent: stats.pendingLeaveRequests > 5,
      onClick: () => handleNavigation('leave-management')
    },
    {
      title: 'Pending Timesheets',
      value: stats.pendingTimesheets || 0,
      icon: <ScheduleIcon />,
      color: 'info.main',
      action: 'Review Timesheets',
      urgent: stats.pendingTimesheets > 10,
      onClick: () => handleNavigation('timesheet-calendar')
    },
    {
      title: 'Active Warnings',
      value: stats.activeWarnings || 0,
      icon: <AssignmentIcon />,
      color: 'error.main',
      action: 'Manage Discipline',
      urgent: stats.activeWarnings > 3,
      onClick: () => handleNavigation('employee-discipline')
    },
    {
      title: 'Unexcused Absences',
      value: stats.unexcusedAbsences || 0,
      icon: <WarningIcon />,
      color: 'error.dark',
      action: 'View Absences',
      urgent: stats.unexcusedAbsences > 5,
      onClick: () => handleNavigation('absence-tracking')
    },
    {
      title: 'Avg Performance Rating',
      value: stats.averageRating ? `${stats.averageRating.toFixed(1)}/5` : 'N/A',
      icon: <TrendingUpIcon />,
      color: stats.averageRating >= 4 ? 'success.main' : stats.averageRating >= 3 ? 'warning.main' : 'error.main',
      action: 'View Reviews',
      onClick: () => handleNavigation('performance-reviews')
    },
    {
      title: 'Completed Reviews',
      value: stats.completedReviews || 0,
      icon: <TrendingUpIcon />,
      color: 'success.main',
      action: 'View Reviews',
      trend: `${stats.completedReviews} this quarter`,
      onClick: () => handleNavigation('performance-reviews')
    },
    {
      title: 'HR Documents',
      value: stats.totalDocuments || 0,
      icon: <FolderIcon />,
      color: 'secondary.main',
      action: 'Manage Documents',
      trend: '45 files stored',
      onClick: () => handleNavigation('hr-documents')
    }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }} data-demo-target="page-hr-dashboard-root">
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 'bold',
            color: '#000062',
            fontSize: { xs: '1.5rem', md: '2.125rem' }
          }}
        >
          HR Management Dashboard
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadDashboardData}
          disabled={loading}
          sx={{ minWidth: 'auto' }}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Key Metrics Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card 
            onClick={statsCards[0].onClick}
            role="button"
            tabIndex={0}
            aria-label={statsCards[0].action}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                statsCards[0].onClick();
              }
            }}
            sx={{ 
              height: '100%',
              cursor: 'pointer',
              transition: 'transform 0.2s',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ color: 'white', opacity: 0.9 }}>
                  {statsCards[0].title}
                </Typography>
                <Avatar sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  width: 48, 
                  height: 48,
                  '& .MuiSvgIcon-root': { fontSize: '1.5rem' }
                }}>
                  {statsCards[0].icon}
                </Avatar>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                {statsCards[0].value}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {statsCards[0].trend}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card 
            onClick={statsCards[1].onClick}
            sx={{ 
              height: '100%',
              cursor: 'pointer',
              transition: 'transform 0.2s',
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ color: 'white', opacity: 0.9 }}>
                  {statsCards[1].title}
                </Typography>
                <Avatar sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  width: 48, 
                  height: 48,
                  '& .MuiSvgIcon-root': { fontSize: '1.5rem' }
                }}>
                  {statsCards[1].icon}
                </Avatar>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                {statsCards[1].value}
              </Typography>
              {statsCards[1].urgent && (
                <Chip 
                  label="Action Required" 
                  size="small" 
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    color: 'white',
                    fontWeight: 'bold'
                  }} 
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card 
            onClick={statsCards[2].onClick}
            sx={{ 
              height: '100%',
              cursor: 'pointer',
              transition: 'transform 0.2s',
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ color: 'white', opacity: 0.9 }}>
                  {statsCards[2].title}
                </Typography>
                <Avatar sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  width: 48, 
                  height: 48,
                  '& .MuiSvgIcon-root': { fontSize: '1.5rem' }
                }}>
                  {statsCards[2].icon}
                </Avatar>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                {statsCards[2].value}
              </Typography>
              {statsCards[2].urgent && (
                <Chip 
                  label="Action Required" 
                  size="small" 
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    color: 'white',
                    fontWeight: 'bold'
                  }} 
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card 
            onClick={statsCards[5].onClick}
            sx={{ 
              height: '100%',
              cursor: 'pointer',
              transition: 'transform 0.2s',
              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
              color: 'white',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ color: 'white', opacity: 0.9 }}>
                  {statsCards[5].title}
                </Typography>
                <Avatar sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  width: 48, 
                  height: 48,
                  '& .MuiSvgIcon-root': { fontSize: '1.5rem' }
                }}>
                  {statsCards[5].icon}
                </Avatar>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                {statsCards[5].value}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Performance Rating
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* HR Management Modules */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <PeopleIcon color="primary" /> HR Management Modules
          </Typography>
          
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Card 
                onClick={() => handleNavigation('employees')}
                role="button"
                tabIndex={0}
                aria-label="Employee Management"
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleNavigation('employees');
                  }
                }}
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: '1px solid #e0e0e0',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4,
                    borderColor: 'primary.main'
                  }
                }}
              >
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  <Avatar sx={{ 
                    bgcolor: 'primary.main', 
                    width: 64, 
                    height: 64, 
                    mx: 'auto', 
                    mb: 2,
                    '& .MuiSvgIcon-root': { fontSize: '2rem' }
                  }}>
                    <PeopleIcon />
                  </Avatar>
                  <Typography variant="h6" gutterBottom>
                    Employee Management
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Manage employee records, departments, and job titles
                  </Typography>
                  <Button variant="contained" size="small">
                    Manage Employees
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Card 
                onClick={() => handleNavigation('leave-management')}
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: '1px solid #e0e0e0',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4,
                    borderColor: 'warning.main'
                  }
                }}
              >
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  <Avatar sx={{ 
                    bgcolor: 'warning.main', 
                    width: 64, 
                    height: 64, 
                    mx: 'auto', 
                    mb: 2,
                    '& .MuiSvgIcon-root': { fontSize: '2rem' }
                  }}>
                    <EventIcon />
                  </Avatar>
                  <Typography variant="h6" gutterBottom>
                    Leave Management
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Handle leave requests, approvals, and vacation tracking
                  </Typography>
                  <Button variant="contained" color="warning" size="small">
                    Manage Leave
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Card 
                onClick={() => handleNavigation('timesheet-calendar')}
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: '1px solid #e0e0e0',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4,
                    borderColor: 'info.main'
                  }
                }}
              >
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  <Avatar sx={{ 
                    bgcolor: 'info.main', 
                    width: 64, 
                    height: 64, 
                    mx: 'auto', 
                    mb: 2,
                    '& .MuiSvgIcon-root': { fontSize: '2rem' }
                  }}>
                    <ScheduleIcon />
                  </Avatar>
                  <Typography variant="h6" gutterBottom>
                    Timesheet Calendar
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Visual timesheet management and approval workflow
                  </Typography>
                  <Button variant="contained" color="info" size="small">
                    Review Timesheets
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Card 
                onClick={() => handleNavigation('absence-tracking')}
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: '1px solid #e0e0e0',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4,
                    borderColor: 'error.main'
                  }
                }}
              >
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  <Avatar sx={{ 
                    bgcolor: 'error.main', 
                    width: 64, 
                    height: 64, 
                    mx: 'auto', 
                    mb: 2,
                    '& .MuiSvgIcon-root': { fontSize: '2rem' }
                  }}>
                    <WarningIcon />
                  </Avatar>
                  <Typography variant="h6" gutterBottom>
                    Absence Tracking
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Monitor attendance and track absence patterns
                  </Typography>
                  <Button variant="contained" color="error" size="small">
                    Track Absences
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Card 
                onClick={() => handleNavigation('employee-discipline')}
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: '1px solid #e0e0e0',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4,
                    borderColor: 'error.dark'
                  }
                }}
              >
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  <Avatar sx={{ 
                    bgcolor: 'error.dark', 
                    width: 64, 
                    height: 64, 
                    mx: 'auto', 
                    mb: 2,
                    '& .MuiSvgIcon-root': { fontSize: '2rem' }
                  }}>
                    <AssignmentIcon />
                  </Avatar>
                  <Typography variant="h6" gutterBottom>
                    Employee Discipline
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Manage warnings, disciplinary actions, and points system
                  </Typography>
                  <Button variant="contained" color="error" size="small">
                    Manage Discipline
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Card 
                onClick={() => handleNavigation('performance-reviews')}
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: '1px solid #e0e0e0',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4,
                    borderColor: 'success.main'
                  }
                }}
              >
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  <Avatar sx={{ 
                    bgcolor: 'success.main', 
                    width: 64, 
                    height: 64, 
                    mx: 'auto', 
                    mb: 2,
                    '& .MuiSvgIcon-root': { fontSize: '2rem' }
                  }}>
                    <TrendingUpIcon />
                  </Avatar>
                  <Typography variant="h6" gutterBottom>
                    Performance Reviews
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Conduct employee evaluations and goal setting
                  </Typography>
                  <Button variant="contained" color="success" size="small">
                    View Reviews
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Card 
                onClick={() => handleNavigation('hr-documents')}
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: '1px solid #e0e0e0',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4,
                    borderColor: 'secondary.main'
                  }
                }}
              >
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  <Avatar sx={{ 
                    bgcolor: 'secondary.main', 
                    width: 64, 
                    height: 64, 
                    mx: 'auto', 
                    mb: 2,
                    '& .MuiSvgIcon-root': { fontSize: '2rem' }
                  }}>
                    <FolderIcon />
                  </Avatar>
                  <Typography variant="h6" gutterBottom>
                    HR Documents
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Store and manage employee documents and policies
                  </Typography>
                  <Button variant="contained" color="secondary" size="small">
                    Manage Documents
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Recent Activities */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeIcon /> Recent Activities
          </Typography>
          
          {recentActivities.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <AccessTimeIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography color="textSecondary" variant="h6">
                No recent activities
              </Typography>
              <Typography color="textSecondary" variant="body2">
                Activities will appear here as they occur
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Activity</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentActivities.map((activity, index) => (
                    <TableRow
                      key={index}
                      hover
                      onClick={() => setSelectedActivity(activity)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {activity.icon}
                          <Typography variant="body2">
                            {activity.title}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {new Date(activity.date).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={activity.status}
                          size="small"
                          color={
                            activity.status === 'pending' ? 'warning' :
                            activity.status === 'approved' ? 'success' :
                            activity.status === 'rejected' ? 'error' : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View activity details">
                          <IconButton
                            size="small"
                            aria-label={`View details for ${activity.title}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedActivity(activity);
                            }}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Activity Detail Dialog (FB-059) */}
      <Dialog
        open={Boolean(selectedActivity)}
        onClose={() => setSelectedActivity(null)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="hr-activity-dialog-title"
      >
        <DialogTitle id="hr-activity-dialog-title">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedActivity?.icon}
            <Typography component="span" variant="h6">
              {selectedActivity?.title || 'Activity Details'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedActivity && (
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="textSecondary">Status</Typography>
                <Chip
                  label={selectedActivity.status || 'unknown'}
                  size="small"
                  color={
                    selectedActivity.status === 'pending' ? 'warning' :
                    selectedActivity.status === 'approved' ? 'success' :
                    selectedActivity.status === 'rejected' ? 'error' : 'default'
                  }
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="textSecondary">Date</Typography>
                <Typography variant="body2">
                  {selectedActivity.date ? new Date(selectedActivity.date).toLocaleString() : '-'}
                </Typography>
              </Box>
              <Divider />
              {(selectedActivity.details || []).map((detail) => (
                <Box
                  key={detail.label}
                  sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}
                >
                  <Typography variant="body2" color="textSecondary" sx={{ flexShrink: 0 }}>
                    {detail.label}
                  </Typography>
                  <Typography variant="body2" sx={{ textAlign: 'right' }}>
                    {detail.value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedActivity(null)} aria-label="Close activity details">
            Close
          </Button>
          {selectedActivity?.navigateTo && (
            <Button
              variant="contained"
              startIcon={<OpenInNewIcon />}
              aria-label={`Open ${selectedActivity.navigateTo} module`}
              onClick={() => {
                const target = selectedActivity.navigateTo;
                setSelectedActivity(null);
                handleNavigation(target);
              }}
            >
              Open in Module
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HRDashboard;
